import {
  InputBaseComponentProps,
  InputBaseProps,
  InputProps,
  SelectChangeEvent,
} from "@mui/material";
// @ts-ignore
import imask from "imask";
import { observer } from "mobx-react";
import * as React from "react";
// @ts-ignore
import { IMaskInput } from "react-imask";
import Localization from "../core/Localization";
import Sys from "../core/Sys";
import getFieldHelperText from "../coreui/FieldHelperText";
import TextFieldWithOverflowLabel from "./TextFieldWithOverflowLabel";

interface MaskedPattern {
  isComplete: boolean;
  mask: string;
  unmaskedValue: string;
  value: string;
}

interface Props {
  autoComplete?: string;
  dataCase: "lower" | "UPPER" | "Any";
  getErrors?: (value: string) => string[];
  helperText?: string;
  icon?: string;
  iconColor?: string;
  InputProps?: Partial<InputProps>;
  inputProps?: Partial<InputBaseProps["inputProps"]>;
  inputRef?: React.Ref<HTMLInputElement>;
  label?: string;
  mask: string;
  name?: string;
  onChange: (value: string) => void;
  required?: boolean;
  style?: React.CSSProperties;
  value: string;
  variant?: "filled" | "standard";
}

interface State {
  lazy?: boolean;
  localErrors?: string[];
}

export class EditMask extends React.Component<Props, State> {
  private static readonly patternDefinitions = {
    "#": /\d/,
    "@": /[A-Za-z]/,
    a: /[A-Za-z0-9]/,
    A: /[A-Za-z0-9]/,
    x: /\S/,
    X: /\S/,
  };
  private readonly componentId: string;
  private patternMask: MaskedPattern | null = null;
  private readonly placeholderChar = "_";
  private inputEl: HTMLInputElement | null = null;

  private handleAccept = (value: string) => {
    this.props.onChange(value);
    if (document.activeElement != this.inputEl) {
      this.setState({ lazy: !value });
    }
  };

  private handleNativeFocus = () => {
    this.setState({ lazy: false });
    if (!this.patternMask) return;
    this.patternMask.unmaskedValue = this.props.value as any;

    // iOS VoiceOver moves the cursor to the end of the mask without triggering
    // a "selectionchange" event - we have to move the cursor back.
    if (Sys.isMobile && Sys.isSafari) {
      setTimeout(this.handleNativeCursorChange, 1000);
    }
  };

  // Do not allow the cursor to move beyond the real input value into placeholder
  // characterss. Ex: 780-___-_|___ move the cursor to 780-|___-____
  private handleNativeCursorChange = () => {
    if (!this.inputEl) {
      return;
    }

    // Do nothing if there is a selection rather than a cursor:
    if (this.inputEl.selectionStart != this.inputEl.selectionEnd) {
      return;
    }

    const placeholderIndex = this.inputEl.value.indexOf(this.placeholderChar);
    if (placeholderIndex == -1) {
      return;
    } else if ((this.inputEl.selectionEnd ?? 0) > placeholderIndex) {
      this.inputEl.setSelectionRange(placeholderIndex, placeholderIndex);
    }
  };

  private createCustomInput = React.forwardRef(
    (
      props: InputBaseComponentProps,
      forwardedRef: React.ForwardedRef<HTMLInputElement>,
    ): JSX.Element => {
      const { onChange, ...other } = props;
      return (
        <IMaskInput
          {...other}
          definitions={EditMask.patternDefinitions}
          lazy={this.state.lazy}
          mask={this.props.mask}
          placeholderChar={this.placeholderChar}
          // Switched to using onAccept as it fires on every mask update
          // (typing, deleting, or programmatic), even when lazy mode would
          // suppress React’s onChange. onAccept gives a live mask instance &
          // raw (underscore-free) value so we can call props.onChange(value),
          // stash the instance for later, and let our focus/blur handlers
          // re-render the full placeholder mask and reposition the cursor correctly
          // for user input and screen readers
          onAccept={this.handleAccept}
          inputRef={(el) => {
            this.inputEl = el;
            // forward to IMask & parent
            if (forwardedRef) {
              if (typeof forwardedRef === "function") forwardedRef(el!);
              else
                (
                  forwardedRef as React.MutableRefObject<HTMLInputElement | null>
                ).current = el;
            }
            // attach native listeners
            if (el) {
              el.removeEventListener("focus", this.handleNativeFocus, true);
              el.addEventListener("focus", this.handleNativeFocus, true);
              el.removeEventListener(
                "selectionchange",
                this.handleNativeCursorChange,
                true,
              );
              el.addEventListener(
                "selectionchange",
                this.handleNativeCursorChange,
                true,
              );
            }
          }}
          prepare={(v: string) => {
            switch (this.props.dataCase) {
              case "lower":
                return v.toLowerCase();
              case "UPPER":
                return v.toUpperCase();
              default:
                return v;
            }
          }}
          unmask={true}
        />
      );
    },
  );

  private static createMaskedPattern(
    mask: string,
    placeholderChar: string,
  ): MaskedPattern {
    return new imask.MaskedPattern({
      definitions: EditMask.patternDefinitions,
      lazy: false,
      mask,
      placeholderChar,
    });
  }

  public static formatValue(mask: string, value: string): string {
    const patternMask = EditMask.createMaskedPattern(mask, " ");

    patternMask.unmaskedValue = value;

    return patternMask.value;
  }

  public static getErrors(
    mask: string,
    value: string | null,
    patternMask: MaskedPattern | null = null,
  ): string[] {
    if (!value) {
      return [];
    }

    const result: string[] = [];

    let maskedPattern = patternMask;
    if (!maskedPattern) {
      maskedPattern = EditMask.createMaskedPattern(mask, " ");
    }
    maskedPattern.unmaskedValue = value;

    if (!maskedPattern.isComplete) {
      result.push(Localization.getBuiltInMessage("incompleteEditMask"));
    }

    return result;
  }

  public constructor(props: Props) {
    super(props);

    this.state = { lazy: true, localErrors: [] };
    this.componentId = `edit-mask-${Sys.nextId}`;
  }

  private announceErrors(errors: string[]): void {
    if (errors.length > 0) {
      Sys.announce(errors.join("; "));
    }
  }

  private getErrors = () => {
    if (!this.props.getErrors) {
      return [];
    }

    let errors: string[] = [...this.props.getErrors(this.props.value)];
    errors = errors.concat(this.state.localErrors!);

    return errors;
  };

  private setLocalErrors() {
    const localErrors: string[] = EditMask.getErrors(
      this.props.mask,
      this.props.value,
      this.patternMask,
    );

    this.setState({ localErrors });
  }

  public componentDidMount() {
    this.patternMask = EditMask.createMaskedPattern(
      this.props.mask,
      this.placeholderChar,
    );
    this.setState({ lazy: !this.props.value });
  }

  public componentWillUnmount() {
    if (this.inputEl) {
      this.inputEl.removeEventListener("focus", this.handleNativeFocus, true);
      this.inputEl.removeEventListener(
        "selectionchange",
        this.handleNativeCursorChange,
        true,
      );
    }
  }

  public render() {
    const fieldHelperText = getFieldHelperText({
      getErrors: this.getErrors,
      helperText: this.props.helperText,
    });

    const onBlur = () => {
      this.setLocalErrors();
      this.announceErrors(fieldHelperText.errors);
      this.setState({ lazy: !this.props.value });
    };

    return (
      <TextFieldWithOverflowLabel
        autoComplete={this.props.autoComplete}
        error={fieldHelperText.hasErrors}
        FormHelperTextProps={{
          "aria-hidden": true,
          ...fieldHelperText.formHelperTextProps,
        }}
        fullWidth
        helperText={fieldHelperText.helperText}
        id={this.componentId}
        InputProps={{
          ...this.props.InputProps,
          inputComponent: this.createCustomInput,
        }}
        inputProps={this.props.inputProps}
        inputRef={this.props.inputRef}
        label={this.props.label}
        name={this.props.name}
        onBlur={onBlur}
        required={this.props.required}
        style={this.props.style}
        value={this.props.value}
        variant={this.props.variant}
      />
    );
  }
}

export default observer(EditMask);
